/**
 * Alert System v1 client — the live fleet-health board + ownership actions
 * (T-036/T-037/T-038/T-040). Kept in its own module rather than growing adminApi.ts
 * (already well over the file-size guideline); reuses adminFetch for auth/env/errors.
 */
import { adminFetch } from './adminApi';
import type { AlertRuntime } from './alertWording';
import type { UnitLiveStatus } from './unitLiveStatus';

export type AlertSeverity = 'ok' | 'amber' | 'red' | 'critical' | 'unavailable';

/** A teammate attached to an alert besides its owner. */
export interface AlertWatcher {
  userId: string;
  name: string | null;
  role: 'backup' | 'subscriber';
}

/** The ownership/mute state attached to a condition on the board. */
export interface AlertInstanceState {
  instanceId: string;
  openedAt: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  claimedAt: string | null;
  mutedUntil: string | null;
  escalationStage: string;
  /** Backups + followers (#764). Absent/empty when nobody is watching. */
  watchers?: AlertWatcher[];
}

/** One active condition on a unit (battery | battery_at_risk | connection | stale). */
export interface AlertCondition {
  key: 'battery' | 'battery_at_risk' | 'connection' | 'stale';
  severity: AlertSeverity;
  suppressed?: boolean;
  instance?: AlertInstanceState | null;
  // signal-specific fields (present depending on key)
  soc?: number | null;
  runwayMinutes?: number | null;
  loadW?: number | null;
  batteryW?: number | null;
  loadClass?: string;
  capacityKwh?: number | null;
  state?: string;
  offlineMinutes?: number | null;
  offlineSince?: string | null;
  minutesAgo?: number | null;
  lastUpdateIso?: string | null;
  /** battery_at_risk: minutes since the Sol-Ark reading was captured (runway is already reduced by it). */
  sampleAgeMinutes?: number | null;
  /** battery_at_risk unknown: 'no_current_reading' = draining at last report, no current reading. */
  reason?: string;
  /** battery: the power left at current use (server-computed; Eric 2026-11-01). */
  runtime?: AlertRuntime | null;
}

/** Why a unit is quiet (server-classified, T-058 #3). Open string on the wire. */
export type ReportingState = 'never_linked' | 'never_reported' | 'stale' | 'reporting';
export interface AlertReporting {
  state: ReportingState | string;
  /** never_linked: unit created; never_reported: device row linked; else last reading. */
  since?: string | null;
  solark?: { state: ReportingState | string; since?: string | null };
  starlink?: { state: ReportingState | string; since?: string | null };
}

export interface AlertUnit {
  unitId: string;
  unitCode: string;
  companyId?: string | null;
  status?: string | null;
  overall: AlertSeverity;
  /** Absent on an older payload — the board then keeps the plain "Unknown" pill. */
  reporting?: AlertReporting;
  battery?: { severity: AlertSeverity; soc?: number | null; linked?: boolean };
  battery_at_risk?: { severity: AlertSeverity };
  connection?: { severity: AlertSeverity; state?: string };
  stale?: { severity: AlertSeverity };
  conditions: AlertCondition[];
  /** Server-classified charging source (fleet-health flat field); null = not charging. */
  charge_source?: 'solar' | 'shore' | 'shore_likely' | null;
  /** The status icons' data (docs/contracts/unit-live-status.md); null when the server couldn't build it. */
  live_status?: UnitLiveStatus | null;
}

export interface AlertBoard {
  generatedAt: string;
  counts: { critical: number; warning: number; healthy: number; unknown: number };
  units: AlertUnit[];
}

/** GET the live ALERTS board: severity + ownership/mute per condition. Internal-only. */
export async function getAlertBoard(): Promise<AlertBoard> {
  return adminFetch<AlertBoard>('/alerts/board');
}

export type MuteInterval = '15m' | '1h' | '4h' | '8h' | 'until-tomorrow';

export async function claimAlertInstance(id: string) {
  return adminFetch<{ success: boolean; alreadyClaimed: boolean; instance: unknown }>(
    `/alerts/instances/${encodeURIComponent(id)}/claim`, { method: 'POST' });
}
export async function resolveAlertInstance(id: string) {
  return adminFetch<{ success: boolean; alreadyResolved: boolean; instance: unknown }>(
    `/alerts/instances/${encodeURIComponent(id)}/resolve`, { method: 'POST' });
}
export async function watchAlertInstance(id: string, role: AlertWatcher['role']) {
  return adminFetch<{ success: boolean; created: boolean }>(
    `/alerts/instances/${encodeURIComponent(id)}/watch`, { method: 'POST', body: JSON.stringify({ role }) });
}
export async function unwatchAlertInstance(id: string) {
  return adminFetch<{ success: boolean }>(
    `/alerts/instances/${encodeURIComponent(id)}/watch`, { method: 'DELETE' });
}
export async function muteAlertInstance(id: string, interval: MuteInterval) {
  return adminFetch<{ success: boolean; instance: unknown }>(
    `/alerts/instances/${encodeURIComponent(id)}/mute`, { method: 'POST', body: JSON.stringify({ interval }) });
}

// ── Alert recipients (who gets alert emails) ──────────────────────────
export interface AlertRecipientUser {
  user_id: string;
  name: string;
  email: string | null;
  alert_subscribed: boolean;
}
export interface AlertRecipients {
  extra_emails: string[];
  users: AlertRecipientUser[];
}
export async function getAlertRecipients(): Promise<AlertRecipients> {
  return adminFetch<AlertRecipients>('/alerts/recipients');
}
export async function saveAlertRecipientEmails(extraEmails: string[]): Promise<{ extra_emails: string[] }> {
  return adminFetch<{ extra_emails: string[] }>('/alerts/recipients', {
    method: 'PUT',
    body: JSON.stringify({ extra_emails: extraEmails }),
  });
}
export async function setUserAlertSubscribed(userId: string, alertSubscribed: boolean): Promise<{ user_id: string; alert_subscribed: boolean }> {
  return adminFetch<{ user_id: string; alert_subscribed: boolean }>(
    `/alerts/recipients/users/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body: JSON.stringify({ alert_subscribed: alertSubscribed }) },
  );
}

export interface TestAlertResult {
  ok: boolean;
  inAppRecipients: number;
  email: { status: 'sent' | 'not_configured' | 'failed'; recipients?: number; error: string | null } | null;
}

/** Superadmin: fire a test alert through the real delivery path (in-app + email). */
export async function sendTestAlert(): Promise<TestAlertResult> {
  return adminFetch<TestAlertResult>('/alerts/test', { method: 'POST' });
}

/**
 * Superadmin: email yourself a sample of the battery alert email (SAMPLE data; its
 * action links change nothing), to check it on a phone. Goes only to you.
 */
export async function sendSampleBatteryAlertEmail(): Promise<{ ok: boolean; to: string }> {
  return adminFetch<{ ok: boolean; to: string }>('/alerts/test/sample-battery-email', { method: 'POST' });
}

// ── Alert email actions, signed in (Eric, 2026-10-28) ─────────────────────────
// The /alert-action page sends the link's token in the body, never the URL.

export type AlertEmailAction = 'claim' | 'mute24h' | 'resolve' | 'false_reading';

export type AlertEmailActionStatus =
  | 'ready' | 'done' | 'already_owned' | 'closed'
  | 'invalid' | 'expired' | 'not_found' | 'no_access' | 'no_session';

export interface AlertEmailActionView {
  status: AlertEmailActionStatus;
  action?: AlertEmailAction;
  unitCode?: string | null;
  conditionLabel?: string | null;
  reading?: { soc: number; at: string | null } | null;
  /** The alert's owner, when someone owns it. */
  owner?: { name: string | null; isYou: boolean } | null;
  mutedUntil?: string | null;
  /** The signed-in person the action is taken as. */
  signedInAs: string | null;
  /** While muted, a drop below this level still alerts (battery alerts only). */
  deeperBelow?: number | null;
}

/** What an alert email link would do, as the signed-in user. Changes nothing. */
export async function previewAlertEmailAction(token: string): Promise<AlertEmailActionView> {
  return adminFetch<AlertEmailActionView>('/alerts/email-actions/preview', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/** Do what an alert email link says, as the signed-in user. */
export async function confirmAlertEmailAction(token: string): Promise<AlertEmailActionView> {
  return adminFetch<AlertEmailActionView>('/alerts/email-actions/confirm', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}
